(() => {
function initBannerSlideshows(container = document) {
  const slideshows = container.querySelectorAll('[data-banner-slideshow]');

  slideshows.forEach((slideshow) => {
    if (slideshow.dataset.bannerSlideshowInitialized === 'true') return;

    const slides = Array.from(slideshow.querySelectorAll('[data-banner-slide]'));
    const previousButton = slideshow.querySelector('[data-banner-slideshow-previous]');
    const nextButton = slideshow.querySelector('[data-banner-slideshow-next]');
    const dots = Array.from(slideshow.querySelectorAll('[data-banner-slideshow-dot]'));
    const autoplayToggle = slideshow.querySelector('[data-banner-slideshow-autoplay-toggle]');
    const autoplayToggleText = slideshow.querySelector('[data-banner-slideshow-autoplay-toggle-text]');

    if (!slides.length) return;

    slideshow.dataset.bannerSlideshowInitialized = 'true';

    const autoplayEnabled = slideshow.dataset.autoplay === 'true';
    const pauseOnHover = slideshow.dataset.pauseOnHover === 'true';
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const autoplaySpeed = Math.max(
      Number(slideshow.dataset.autoplaySpeed) || 5000,
      3000
    );

    let currentIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
    let autoplayTimer = null;
    let isPaused = false;
    let isEditorPaused = false;
    let isUserPaused = false;

    if (currentIndex < 0) {
      currentIndex = 0;
    }

    function setActiveSlide(nextIndex) {
      const normalizedIndex = (nextIndex + slides.length) % slides.length;

      slides.forEach((slide, index) => {
        const isActive = index === normalizedIndex;

        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      dots.forEach((dot, index) => {
        if (index === normalizedIndex) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      currentIndex = normalizedIndex;
    }

    function setActiveSlideByBlockId(blockId) {
      if (!blockId) return false;

      const slideIndex = slides.findIndex((slide) => slide.dataset.slideBlockId === blockId);

      if (slideIndex < 0) return false;

      setActiveSlide(slideIndex);
      return true;
    }

    function updateAutoplayToggle() {
      if (!autoplayToggle) return;

      const playLabel = slideshow.dataset.playLabel || 'Play slideshow';
      const pauseLabel = slideshow.dataset.pauseLabel || 'Pause slideshow';
      const label = isUserPaused ? playLabel : pauseLabel;

      autoplayToggle.setAttribute('aria-pressed', isUserPaused ? 'true' : 'false');
      autoplayToggle.setAttribute('aria-label', label);

      if (autoplayToggleText) {
        autoplayToggleText.textContent = label;
      }
    }

    function stopAutoplay() {
      if (!autoplayTimer) return;

      window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    }

    function startAutoplay() {
      stopAutoplay();
      updateAutoplayToggle();

      if (!autoplayEnabled || prefersReducedMotion || slides.length < 2 || isUserPaused) return;

      autoplayTimer = window.setInterval(() => {
        if (isPaused || isEditorPaused || isUserPaused) return;

        setActiveSlide(currentIndex + 1);
      }, autoplaySpeed);
    }

    function restartAutoplay() {
      if (isUserPaused) return;

      startAutoplay();
    }

    previousButton?.addEventListener('click', () => {
      setActiveSlide(currentIndex - 1);
      restartAutoplay();
    });

    nextButton?.addEventListener('click', () => {
      setActiveSlide(currentIndex + 1);
      restartAutoplay();
    });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const slideIndex = Number(dot.dataset.slideIndex);

        if (Number.isNaN(slideIndex)) return;

        setActiveSlide(slideIndex);
        restartAutoplay();
      });
    });

    autoplayToggle?.addEventListener('click', () => {
      isUserPaused = !isUserPaused;
      updateAutoplayToggle();

      if (isUserPaused) {
        stopAutoplay();
      } else {
        startAutoplay();
      }
    });

    if (pauseOnHover) {
      slideshow.addEventListener('mouseenter', () => {
        isPaused = true;
      });

      slideshow.addEventListener('mouseleave', () => {
        isPaused = false;
      });

      slideshow.addEventListener('focusin', () => {
        isPaused = true;
      });

      slideshow.addEventListener('focusout', (event) => {
        if (event.relatedTarget && slideshow.contains(event.relatedTarget)) return;

        isPaused = false;
      });
    }

    const handleVisibilityChange = () => {
      isPaused = document.hidden;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    slideshow.setActiveBannerSlide = setActiveSlide;
    slideshow.setActiveBannerSlideByBlockId = setActiveSlideByBlockId;

    slideshow.pauseBannerSlideshowForEditor = () => {
      isEditorPaused = true;
    };

    slideshow.resumeBannerSlideshowAfterEditor = () => {
      isEditorPaused = false;
    };

    slideshow.destroyBannerSlideshow = () => {
      stopAutoplay();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      slideshow.dataset.bannerSlideshowInitialized = 'false';
      delete slideshow.setActiveBannerSlide;
      delete slideshow.setActiveBannerSlideByBlockId;
      delete slideshow.pauseBannerSlideshowForEditor;
      delete slideshow.resumeBannerSlideshowAfterEditor;
      delete slideshow.destroyBannerSlideshow;
    };

    setActiveSlide(currentIndex);
    updateAutoplayToggle();
    startAutoplay();
  });
}

function handleBannerSlideshowBlockSelect(event) {
  const selectedBlock = event.target;
  const slideshow = selectedBlock.closest('[data-banner-slideshow]');

  if (!slideshow) return;

  slideshow.pauseBannerSlideshowForEditor?.();

  const selectedSlide = selectedBlock.closest('[data-banner-slide]');

  if (selectedSlide) {
    const slides = Array.from(slideshow.querySelectorAll('[data-banner-slide]'));
    const slideIndex = slides.indexOf(selectedSlide);

    if (slideIndex >= 0) {
      slideshow.setActiveBannerSlide?.(slideIndex);
      return;
    }
  }

  slideshow.setActiveBannerSlideByBlockId?.(event.detail?.blockId);
}

function handleBannerSlideshowBlockDeselect(event) {
  const selectedBlock = event.target;
  const slideshow = selectedBlock.closest('[data-banner-slideshow]');

  if (!slideshow) return;

  slideshow.resumeBannerSlideshowAfterEditor?.();
}

if (!window.bannerSlideshowGlobalListenersInitialized) {
  window.bannerSlideshowGlobalListenersInitialized = true;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initBannerSlideshows();
    });
  } else {
    initBannerSlideshows();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initBannerSlideshows(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const slideshows = event.target.querySelectorAll('[data-banner-slideshow]');

    slideshows.forEach((slideshow) => {
      if (typeof slideshow.destroyBannerSlideshow === 'function') {
        slideshow.destroyBannerSlideshow();
      }
    });
  });

  document.addEventListener('shopify:block:select', handleBannerSlideshowBlockSelect);
  document.addEventListener('shopify:block:deselect', handleBannerSlideshowBlockDeselect);
}
})();
